const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const handleize = (str) => {
  return str
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const updateCartBubble = (itemCount) => {
  const cartBubble = document.getElementById("CartBubble");
  if (!cartBubble) return;

  if (itemCount) {
    cartBubble.classList.remove("hidden");
    cartBubble.textContent = itemCount;
  } else {
    cartBubble.classList.add("hidden");
    cartBubble.textContent = "";
  }
}

// Size selector
class PfSizeSelector extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.sizeInputs.forEach(input => {
      input.addEventListener("change", this.onSizeChange.bind(this), { signal });
    });
  }

  get sizeInputs() {
    return this.querySelectorAll("input[type='radio']");
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  onSizeChange(e) {
    const size = {
      name: e.target.dataset.size,
      variant_id: e.target.value,
      quantity: e.target.dataset.quantity
    };

    this.selectedSize = size;
    this.dataset.selected = true;

    this.dispatchEvent(new CustomEvent('sizeselect', {
      detail: size,
      bubbles: true,
      composed: true
    }));
  }
}

if(!customElements.get("pf-size-selector")) {
  customElements.define("pf-size-selector", PfSizeSelector);
}

// Bundle selector
class PfBundleSelector extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-bundle-item]')];
    this.activeItem = this.querySelector('.pf-active');
    this.regularPrice = this.dataset.regularPrice;

    this.#bindEvents();

    if (this.activeItem) {
      const bundle = this.selectedBundle;
      this.#updateHeaderPrice(bundle, true);
      this.#updateElementData(bundle);
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get selectedBundle() {
    return this.activeItem ? this.#extractBundleData(this.activeItem) : null;
  }

  set selectedBundle(element) {
    if (!(element instanceof HTMLElement)) return;

    this.#setActiveItem(element);
  }

  get quantity() {
    const quantityEl = document.querySelector("pf-purchase-controls input.pf-quantity-count");
    return parseInt(quantityEl?.value || 1);
  }

  get regularPriceEl() {
    return document.querySelector(".pf-prodinfo-price .pf-price-regular");
  }

  #bindEvents() {
    this.items.forEach(item => {
      item.addEventListener(
        'click',
        () => this.selectedBundle = item,
        { signal: this.#controller.signal }
      );
    });

    document.addEventListener(
      'quantitychange',
      (e) => this.#onQuantityChange(e.detail.quantity, e.detail.manual),
      { signal: this.#controller.signal }
    );
  }

  #setActiveItem(newActive) {
    this.items.forEach(item => item.classList.remove('pf-active'));
    newActive.classList.add('pf-active');

    this.activeItem = newActive;
    const bundle = this.selectedBundle;

    this.#updateHeaderPrice(bundle, false);
    this.#updateElementData(bundle);

    this.dispatchEvent(new CustomEvent('bundlechange', {
      detail: bundle,
      bubbles: true,
      composed: true
    }));
  }

  #extractBundleData(item) {
    return {
      title: item.querySelector('.pf-bundle-title')?.textContent.trim(),
      unitPrice: parseFloat(item.dataset.unitPrice),
      minQty: parseInt(item.dataset.minimumQuantity, 10),
      element: item
    };
  }

  #updateHeaderPrice(bundle, initialUpdate) {
    const discountPrice = document.querySelector(".pf-price-discount");
    if (!discountPrice) return;

    if (bundle.minQty === 1) discountPrice.innerHTML = "";
    else discountPrice.innerHTML = `$${bundle.unitPrice % 1 == 0 ? bundle.unitPrice : bundle.unitPrice.toFixed(2)} ea`;

    if(this.regularPriceEl) {
      setTimeout(() => this.regularPriceEl.dataset.quantity = this.quantity, 20);
    }
  }

  #updateElementData(bundle) {
    if (!bundle) return;

    this.dataset.selectedTitle = bundle.title || "";
    this.dataset.selectedUnitPrice = bundle.unitPrice || "";
    this.dataset.selectedMinQty = bundle.minQty || "";
  }

  #onQuantityChange(qty, manual = false) {
    if(this.regularPriceEl) {
      this.regularPriceEl.dataset.quantity = this.quantity;
    }

    if (!manual) return;
    let closestBundle = null;
    let closestDiff = Infinity;

    for (const item of this.items) {
      const minQty = parseInt(item.dataset.minimumQuantity, 10);

      if (qty >= minQty && qty - minQty < closestDiff) {
        closestBundle = item;
        closestDiff = qty - minQty;
      }
    }

    if (closestBundle && closestBundle !== this.activeItem) {
      this.selectedBundle = closestBundle;
    }
  }
}

if (!customElements.get('pf-bundle-selector')) {
  customElements.define('pf-bundle-selector', PfBundleSelector);
}

// Purchase controls
class PfPurchaseControls extends HTMLElement {
  #controller = new AbortController();
  #isManualChange = false;

  connectedCallback() {
    this.isSubscriptions = this.dataset.isSubscriptions == "true";
    this.qtyInput = this.querySelector('[qty-count]');
    this.decrementBtn = this.querySelector('[qty-decrement]');
    this.incrementBtn = this.querySelector('[qty-increment]');
    this.cartBtn = this.querySelector(".pf-cartbtn-trigger");
    this.bundleSelector = document.querySelector('pf-bundle-selector');

    this.updatePrice();

    this.#bindEvents();
    if (this.bundleSelector?.selectedBundle)
      this.#setQuantity(this.bundleSelector.selectedBundle.minQty);
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  updatePrice() {
    const bundleExists = document.querySelector("pf-bundle-selector");
    if(bundleExists) return;

    const discountPriceEl = document.querySelector(".pf-prodinfo-price .pf-price-discount");
    const regularPriceEl = document.querySelector(".pf-prodinfo-price .pf-price-regular");

    if(regularPriceEl.dataset.value == discountPriceEl.dataset.value || !regularPriceEl.dataset.value || !discountPriceEl.dataset.value) return;

    regularPriceEl.textContent = regularPriceEl.dataset.value;
    discountPriceEl.textContent = discountPriceEl.dataset.value;
  }

  #bindEvents() {
    this.decrementBtn?.addEventListener(
      'click',
      () => this.#onManualQuantityChange(-1),
      { signal: this.#controller.signal }
    );

    this.incrementBtn?.addEventListener(
      'click',
      () => this.#onManualQuantityChange(1),
      { signal: this.#controller.signal }
    );

    this.qtyInput?.addEventListener(
      'input',
      (e) => this.#onManualInput(e),
      { signal: this.#controller.signal }
    );

    this.bundleSelector?.addEventListener(
      'bundlechange',
      (e) => this.#onBundleChange(e.detail),
      { signal: this.#controller.signal }
    );

    this.cartBtn?.addEventListener(
      'click',
      (e) => this.#onAddToCart(e),
      { signal: this.#controller.signal }
    );

    document.addEventListener(
      "sizeselect",
      (e) => this.#onSizeSelect(e.detail),
      { signal: this.#controller.signal }
    );
  }

  #onManualQuantityChange(delta) {
    this.#isManualChange = true;
    const newQty = Math.max(1, this.#getQuantity() + delta);

    this.#setQuantity(newQty);
    this.#dispatchQuantityChange(newQty, true);
    this.#resetManualFlag();
  }

  #onManualInput(e) {
    this.#isManualChange = true;

    const value = e.target.value.replace(/\D/g, '');
    const num = parseInt(value || '1', 10);
    const validQty = Math.max(1, num);

    e.target.value = validQty;

    this.#dispatchQuantityChange(validQty, true);
    this.#resetManualFlag();
  }

  #onBundleChange(bundle) {
    if (this.#isManualChange) return;
    if (!bundle) return;
    
    const minQty = parseInt(bundle.minQty, 10) || 1;
    this.#setQuantity(minQty);
  }

  #onSizeSelect(size) {
    const avlQty = Number(size.quantity || 0);

    if(avlQty > 0) {
      this.cartBtn.querySelector("span").innerHTML = "Add to Cart";
      this.cartBtn.setAttribute("size-selected", "");
    } else {
      this.cartBtn.querySelector("span").innerHTML = "Sold Out";
      this.cartBtn.removeAttribute("size-selected");
    }
  }

  #dispatchQuantityChange(qty, manual = false) {
    this.dispatchEvent(new CustomEvent('quantitychange', {
      detail: { quantity: qty, manual },
      bubbles: true,
      composed: true
    }));
  }

  async #onAddToCart(e) {
    e.preventDefault();

    let cartItems = [];
    const colorMixMode = document.querySelector(".pf-colmix-modes")?.dataset.active || "single";
    const quantity = this.#getQuantity();

    if (this.isSubscriptions || colorMixMode === "single") {
      const selectedVariant = document.querySelector(".pf-size-input:checked")?.value;
      const selling_plan = this.isSubscriptions ? document.querySelector(".rc-selling-plans-dropdown__select")?.value : null;

      const item = {
        id: Number(selectedVariant),
        quantity
      };

      if(selling_plan) {
        item.selling_plan = selling_plan;
      }

      if (selectedVariant) {
        cartItems.push(item);
      }
    } else {
      const colorMixer = document.querySelector("pf-color-mixer");
      if (!colorMixer) return;

      const selectedProducts = colorMixer.selectedVariants?.slice(0, quantity) || [];

      for (const curr of selectedProducts) {
        if (!curr || !curr.variantId || curr.quantity < 1) continue;

        const existingItem = cartItems.find(p => p.id === curr.variantId);
        
        if (existingItem) {
          existingItem.quantity += 1
        } else {
          cartItems.push({ id: curr.variantId, quantity: 1 })
        };
      }
    }

    if (!cartItems.length) {
      console.warn("No variants selected to add to cart.");
      return;
    }

    try {
      this.cartBtn?.classList.add("pf-loading");

      const response = await fetch("/cart/add.js?sections=cart-bubble", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({ items: cartItems }),
      });

      const data = await response.json();

      this.cartBtn?.classList.remove("pf-loading");

      if (!response.ok) {
        console.error("Shopify Add to Cart Error:", data);
        return;
      }

      updateCartBubble(data.sections["cart-bubble"].match(/\d+/));

      document.documentElement.dispatchEvent(
        new CustomEvent("product:added-to-cart", { bubbles: true })
      );

      await sleep(250);

      document.querySelector("cart-modal").show();
    } catch (error) {
      this.cartBtn?.classList.remove("pf-loading");
      
      console.error("Network or fetch error:", error);
    }
  }

  #resetManualFlag() {
    queueMicrotask(() => (this.#isManualChange = false));
  }

  #getQuantity() {
    return parseInt(this.qtyInput?.value || '1', 10);
  }

  #setQuantity(value) {
    if (this.qtyInput) this.qtyInput.value = Math.max(1, parseInt(value, 10) || 1);
  }
}

if (!customElements.get('pf-purchase-controls')) {
  customElements.define('pf-purchase-controls', PfPurchaseControls);
}


// Color mixer
class PfColorMixer extends HTMLElement {
  #controller = new AbortController();
  #productsPromise = null;
  products = null;
  flatProducts = [];
  selectedVariants = [];

  connectedCallback() {
    const { signal } = this.#controller;

    this.#productsPromise = this.fetchProductsData();

    this.renderVariantTiles();
    this.colorModeWrapper?.addEventListener("click", this.toggleColorMode.bind(this), { signal });
    this.sliderWrapper?.addEventListener("click", this.addProductToBundle.bind(this), { signal });
    this.tilesWrapper?.addEventListener("click", this.removeProductFromBundle.bind(this), { signal });
    
    document.addEventListener("quantitychange", this.resetBundleRender.bind(this), { signal });
    document.addEventListener("bundlechange", this.resetBundleRender.bind(this), { signal });
    document.addEventListener("sizeselect", this.renderColorPicker.bind(this), { signal });
    document.addEventListener("product:added-to-cart", this.resetColorMixer.bind(this), { signal });

    if(this.isMixedTabDefault) {
      const bundleSelector = document.querySelector("pf-bundle-selector");
      if(!bundleSelector) return;

      if(parseInt(bundleSelector.dataset.selectedMinQty || 1) > 1) {
        this.colorModeWrapper.setAttribute("data-active", "mixed");
      }
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get colorModeWrapper() {
    return this.querySelector("[data-color-modes]");
  }

  get tilesWrapper() {
    return this.querySelector("[data-tiles-wrapper]");
  }

  get sliderWrapper() {
    return this.querySelector("[data-color-slider-wrapper]");
  }

  get sliderEl() {
    return this.querySelector("[data-slider]");
  }

  get variantPicker() {
    return document.querySelector("product-variant-picker");
  }

  get quantity() {
    const quantityEl = document.querySelector("pf-purchase-controls input.pf-quantity-count");
    return parseInt(quantityEl?.value || 1);
  }

  get mixerMode() {
    const modes = {
      reset_mixer: "none",
      color_only: "color",
      color_and_size: "both"
    };

    return modes[this.dataset.mixerMode];
  }

  get isMixedTabDefault() {
    return this.dataset.mixedTabDefault == "true";
  }

  async fetchProductsData() {
    await customElements.whenDefined("product-variant-picker");
    if (!this.variantPicker) return null;

    const data = await this.variantPicker.getProductData();
    this.products = data;

    return data;
  }

  async getProducts() {
    if (this.products) return this.products;

    if (!this.#productsPromise) {
      this.#productsPromise = this.fetchProductsData();
    }

    return this.#productsPromise;
  }

  toggleColorMode(e) {
    const targetBtn = e.target.closest(".pf-colmix-toggle");
    if(!targetBtn || this.colorModeWrapper.dataset.active == targetBtn.dataset.mode) return;

    this.colorModeWrapper.setAttribute("data-active", targetBtn.dataset.mode);

    this.updateAtcButton();
  }

  async renderColorPicker(e) {
    const currentProductId = Number(this.dataset.productId);

    await this.getProducts();

    this.flatProducts = this.products.map(product => {
      const currentVariant = product.variants.find(v => {
        return v.options[1] == e.detail?.name;
      });

      const newProduct = {
        handle: product.handle,
        productId: product.id,
        color: currentVariant?.options[0],
        variantId: currentVariant?.id || null,
        variantName: currentVariant?.id ? e.detail?.name : null,
        quantity: currentVariant?.quantity || 0,
        image: currentVariant?.featured_image?.src || product.featured_image,
        pattern: product.pattern || null
      }

      return newProduct;
    });

    let slides = "";

    // this.flatProducts.sort((a, b) => {
    //   if (a.productId === currentProductId) return -1;
    //   if (b.productId === currentProductId) return 1;
    //   return 0;
    // });

    this.flatProducts.sort((a, b) => {
      if (a.productId === currentProductId) return -1;
      if (b.productId === currentProductId) return 1;

      const order = p =>
        p.pattern === "solid" ? 0 :
        p.pattern === "pattern" ? 1 : 
        2;

      return order(a) - order(b);
    });


    this.flatProducts
    .filter(product => product.quantity > 0)
    .forEach(product => {
      const currentColor = handleize(product.color);
      const swatchLines = this.dataset.colorSwatches?.split(/\r?\n/);

      let colorValue = null;
      swatchLines?.forEach(line => {
        const [name, value] = line.split(":").map(s => s.trim());
        if (!name || !value) return;
        
        if (currentColor == handleize(name)) {
          colorValue = value;
        }
      });

      let style = /\.(jpg|png)$/i.test(colorValue)
        ? `background-image: url('https://cdn.shopify.com/s/files/1/0645/1996/2668/files/${colorValue}')`
        : `background: linear-gradient(to right, ${colorValue}, ${colorValue})`;

      if (!colorValue) style = `background-image: url('${product.image}')`;


      slides += `
        <div class="swiper-slide">
          <button 
            type="button" 
            class="pf-colmix-btn" 
            style="${style};"
            data-variant-id="${product.variantId}"
            data-pattern="${product.pattern}"
          ></button>
        </div>
      `;
    });

    this.sliderWrapper.innerHTML = slides;

    this.initSlider();

    if(this.mixerMode == "none") {
      this.selectedVariants = [];
      // return this.renderVariantTiles({ resetFirst: "no" });
    } else if(this.selectedVariants.length == 0) {
      this.selectedVariants[0] = this.flatProducts[0];
    } else if(this.mixerMode == "color") {
      this.selectedVariants = this.selectedVariants.map(sv => {
        if (!sv) return null;

        const newSv = this.flatProducts.find(prd => {
          return prd.handle === sv.handle;
        });

        return newSv;
      });
    }

    this.renderVariantTiles();
  }

  toggleCartBtn(disabled = true, btnText = "Add to Cart", title = "") {
    const cartBtn = document.querySelector(".pf-cartbtn-trigger");
    if(!cartBtn) console.log("No cart btn");

    cartBtn.toggleAttribute("disabled", disabled);
    
    if(!cartBtn.hasAttribute("size-selected")) {
      btnText = "Select Size";
    }
    
    cartBtn.querySelector("span").innerText = btnText;
    
    if(title) {
      cartBtn.closest(".pf-cartbtn-wrapper")?.setAttribute("title", title);
    } else {
      cartBtn.closest(".pf-cartbtn-wrapper")?.removeAttribute("title");
    }
  }

  updateAtcButton() {
    const colorMixMode = document.querySelector(".pf-colmix-modes")?.dataset.active || "single";
    const selectedCount = this.selectedVariants.slice(0, this.quantity).filter(p => p != null).length;

    if(this.quantity > selectedCount && colorMixMode == "mixed") {
      // this.toggleCartBtn(true, `Select items (${selectedCount}/${this.quantity})`);
      // this.toggleCartBtn(true, "Add to Cart", `Add ${this.quantity - selectedCount} more items`);
      this.toggleCartBtn(true, "Add to Cart");
    } else {
      this.toggleCartBtn(false);
    }
  }

  renderVariantTiles(options) {
    this.toggleAttribute("data-hidden", Math.max(parseInt(this.quantity), 1) === 1);

    let tiles = "";

    const empty = this.selectedVariants.every((item, i) => i >= this.quantity || !item);

    if(empty && options?.resetFirst != "no") {
      this.selectedVariants[0] = this.flatProducts[0];
    }

    for(let i = 0; i < this.quantity; i++) {
      const variant = this.selectedVariants[i];

      if(variant) {
        const isUnavailable = this.mixerMode == "color" && variant.quantity <= 0;
        const unavailableClass = isUnavailable ? 'pf-unavailable' : '';
        const unavailableLabel = isUnavailable ? '<span class="pf-colmix-unavailable">Not available in this size</span>' : '';
        
        const variantLabel = this.mixerMode == "both" ? `<span class="pf-colmix-variant">${variant.variantName}</span>` : "";

        tiles += `
          <div
            class="pf-colmix-tile ${unavailableClass}" 
            data-index="${i}"
            data-variant-id="${variant.variantId}" 
            data-quantity="${variant.quantity}"
          >
            <div class="pf-colmix-content">
              <img src="${variant.image}" />
              ${unavailableLabel}
              ${variantLabel}
              <button type="button" class="pf-colmix-remove">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15"><path fill="currentColor" d="M10.969 3.219a.574.574 0 1 1 .812.812L8.313 7.5l3.468 3.469l.074.09a.575.575 0 0 1-.796.796l-.09-.074L7.5 8.312l-3.469 3.47a.574.574 0 1 1-.812-.813L6.688 7.5l-3.47-3.469l-.073-.09a.575.575 0 0 1 .796-.797l.09.075L7.5 6.687z"/></svg>
              </button>
            </div>
          </div>
        `;
      } else {
        tiles += `
          <div
            class="pf-colmix-tile"
            data-index="${i}"
          >
            <div class="pf-colmix-empty">
              <span>+</span>
            </div>
          </div>
        `;
      }
    }

    this.tilesWrapper.innerHTML = tiles;

    this.updateAtcButton();
  }

  resetBundleRender() {
    this.selectedVariants[0] = this.flatProducts[0];
    this.renderVariantTiles();
  }

  addProductToBundle(e) {
    const productBtn = e.target.closest(".pf-colmix-btn");
    if(!productBtn) return;

    const product = this.flatProducts?.find(prd => prd.variantId == productBtn.dataset.variantId);

    for(let i = 0; i < this.quantity; i++) {
      if(!!this.selectedVariants[i]) continue;

      this.selectedVariants[i] = product || null;
      this.renderVariantTiles();
      break;
    }
  }

  removeProductFromBundle(e) {
    const removeBtn = e.target.closest(".pf-colmix-remove");
    if(!removeBtn) return;

    const index =  removeBtn.closest(".pf-colmix-tile")?.dataset.index;

    this.selectedVariants[index] = null;

    this.renderVariantTiles({
      resetFirst: "no"
    });
  }

  initSlider() {
    if(this.slider) return this.slider.update();

    this.slider = new Swiper(this.sliderEl, {
      slidesPerView: "auto",
      spaceBetween: 8,
      slidesOffsetBefore: 20,
      slidesOffsetAfter: 20,
      navigation: {
        prevEl: ".pf-colmix-prev",
        nextEl: ".pf-colmix-next",
      },
      breakpoints: {
        768: {
          spaceBetween: 12,
        }
      }
    });
  }

  resetColorMixer() {
    this.selectedVariants = [this.flatProducts[0]];

    this.renderVariantTiles();

    this.colorModeWrapper.dataset.active = "single";
  }
}

if(!customElements.get("pf-color-mixer")) {
  customElements.define("pf-color-mixer", PfColorMixer);
}

// Pf Purchase Together
class PfPurchaseTogether extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.buttons = this.querySelectorAll(".pf-product-cartbtn");
    this.#bindEvents();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #bindEvents() {
    this.buttons.forEach(btn => {
      btn.addEventListener(
        "click",
        (e) => this.#onAddToCart(e),
        { signal: this.#controller.signal }
      );
    });
  }

  async #onAddToCart(e) {
    const btn = e.currentTarget;
    const variantId = Number(btn.dataset.variantId);
    const unavailable = btn.hasAttribute("data-unavailable");

    if (unavailable || !variantId) return;

    btn.classList.add("pf-loading");

    try {
      const response = await fetch("/cart/add.js?sections=cart-bubble", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }]
        }),
      });

      const data = await response.json();
      btn.classList.remove("pf-loading");

      if (!response.ok) {
        console.error("Frequently Purchased Add to Cart Error:", data);
        return;
      }

      updateCartBubble(data.sections["cart-bubble"].match(/\d+/));

      document.documentElement.dispatchEvent(
        new CustomEvent("product:added-to-cart", { bubbles: true })
      );

      await sleep(250);

      document.querySelector("cart-modal")?.show();

    } catch (error) {
      btn.classList.remove("pf-loading");
      console.error("Network or fetch error:", error);
    }
  }
}

if (!customElements.get("pf-purchase-together")) {
  customElements.define("pf-purchase-together", PfPurchaseTogether);
}


// PF Trust Badges
class PFTrustBadges extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.badges.forEach(badge => {
      if(badge.dataset.scrollSelector == "null") return;

      badge.addEventListener("click", this.onClick.bind(this), { signal });
    })
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get badges() {
    return Array.from(this.querySelectorAll(".pf-tbadge-card"));
  }

  onClick(e) {
    e.preventDefault();

    const scrollEl = document.querySelector(e.currentTarget?.dataset.scrollSelector);
    if(!scrollEl) return;

    scrollEl.scrollIntoView({
      behavior: "smooth",
    });
  }
}

if (!customElements.get("pf-trust-badges")) {
  customElements.define("pf-trust-badges", PFTrustBadges);
}